<template>
  <div>
    <CCard>
      <CCardHeader>
        <strong>Filter 模板管理</strong>
        <div class="float-right">
          <CButtonGroup>
            <CButton color="primary" size="sm" @click="openCreateModal">新建 Chain</CButton>
            <CButton color="success" size="sm" @click="openExtractModal">从路由器导入</CButton>
          </CButtonGroup>
        </div>
      </CCardHeader>
      <CCardBody>
        <!-- scope 筛选 -->
        <div class="mb-3">
          <span class="mr-2 text-muted">Scope 筛选：</span>
          <CButton
            size="sm"
            class="mr-1"
            :color="activeScope === '' ? 'primary' : 'light'"
            @click="activeScope = ''"
          >
            全部 ({{ allChains.length }})
          </CButton>
          <CButton
            size="sm"
            class="mr-1"
            :color="activeScope === 'global' ? 'info' : 'light'"
            @click="activeScope = 'global'"
          >
            🌐 Global ({{ globalCount }})
          </CButton>
          <CButton
            size="sm"
            class="mr-1"
            :color="activeScope === 'local' ? 'warning' : 'light'"
            @click="activeScope = 'local'"
          >
            📍 Local ({{ localCount }})
          </CButton>
        </div>

        <CAlert v-if="activeScope === 'global'" color="info" fade>
          全局 Filter 可跨组共享，需关联到指定路由器组
        </CAlert>
        <CAlert v-else-if="activeScope === 'local'" color="warning" fade>
          本机 Filter 仅同步到关联的路由器
        </CAlert>

        <CDataTable
          :items="chainList"
          :fields="fields"
          hover
          striped
          :loading="loading"
          sorter
          pagination
          :items-per-page="10"
        >
          <template #name="{ item }">
            <td class="align-middle">
              <div>{{ item.display_name || item.name }}</div>
              <small v-if="item.display_name && item.display_name !== item.name" class="text-muted">{{ item.name }}</small>
            </td>
          </template>
          <template #rule_count="{ item }">
            <td class="text-center align-middle">{{ item.rule_count }}</td>
          </template>
          <template #enabled="{ item }">
            <td class="text-center align-middle">
              <CBadge :color="item.enabled ? 'success' : 'secondary'">
                {{ item.enabled ? '启用' : '禁用' }}
              </CBadge>
            </td>
          </template>
          <template #scope="{ item }">
            <td class="text-center align-middle">
              <CBadge :color="item.scope === 'global' ? 'info' : 'warning'">
                {{ item.scope === 'global' ? '🌐 Global' : '📍 Local' }}
              </CBadge>
            </td>
          </template>
          <template #tags="{ item }">
            <td class="text-center align-middle">
              <div class="d-flex flex-wrap justify-content-center" style="gap:3px;">
                <CBadge color="light">{{ item.afi }}</CBadge>
                <CBadge color="light">{{ item.protocol }}</CBadge>
                <CBadge color="light">{{ item.direction }}</CBadge>
              </div>
            </td>
          </template>
          <template #action="{ item }">
            <td class="text-center align-middle">
              <CButtonGroup>
                <CButton color="primary" size="sm" @click="goDetail(item.id)">详情</CButton>
                <CButton color="success" size="sm" @click="syncChain(item.id)">同步</CButton>
                <CButton color="warning" size="sm" @click="toggleChain(item)">{{ item.enabled ? '禁用' : '启用' }}</CButton>
                <CButton color="danger" size="sm" @click="deleteChain(item)">删除</CButton>
              </CButtonGroup>
            </td>
          </template>
        </CDataTable>
      </CCardBody>
    </CCard>

    <!-- 创建 Modal -->
    <CModal :show.sync="createModal.show" :title="'新建 Chain'" color="primary" size="lg">
      <CForm>
        <CInput label="Chain 名称（下发到路由器的名称）" v-model="createModal.form.name" placeholder="如 filter-bgp-ipv4-backbone-out" />
        <CInput label="显示名称（系统记录，方便识别用途）" v-model="createModal.form.display_name" placeholder="留空则与 Chain 名称相同" />
        <CTextarea label="描述" v-model="createModal.form.description" :rows="2" />
        <CRow>
          <CCol sm="4">
            <CSelect label="地址族" v-model="createModal.form.afi" :options="['ipv4','ipv6','dual']" />
          </CCol>
          <CCol sm="4">
            <CSelect label="协议" v-model="createModal.form.protocol" :options="['bgp','ospf','rip','static','mixed']" />
          </CCol>
          <CCol sm="4">
            <CSelect label="方向" v-model="createModal.form.direction" :options="['in','out','both']" />
          </CCol>
        </CRow>
        <CSelect label="Scope" v-model="createModal.form.scope" :options="[
          { value: 'global', label: '🌐 Global 全局' },
          { value: 'local', label: '📍 Local 本机' }
        ]" />
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="createModal.show = false">取消</CButton>
          <CButton color="success" @click="submitCreate">创建</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 从路由器导入 Modal -->
    <CModal :show.sync="extractModal.show" title="从路由器导入 Filter 规则" color="primary" size="lg">
      <CForm>
        <CSelect
          label="源路由器"
          v-model="extractModal.form.router_id"
          :options="routerOptions"
          placeholder="请选择路由器"
          @change="onExtractRouterChange"
        />
        <CSelect
          v-if="extractModal.form.router_id"
          label="路由器上的 Chain"
          v-model="extractModal.form.chain_name"
          :options="extractModal.chainOptions"
          :disabled="extractModal.loadingChains"
          :placeholder="extractModal.loadingChains ? '正在拉取...' : '请选择 Chain'"
          @change="onChainSelected"
        />
        <CAlert v-if="extractModal.chainError" color="danger" fade>{{ extractModal.chainError }}</CAlert>
        <CInput
          v-if="extractModal.form.name"
          label="模板名称（下发到路由器的名称）"
          :value="extractModal.form.name"
          disabled
        />
        <CInput
          v-if="extractModal.form.name"
          label="显示名称（系统记录，方便识别用途）"
          v-model="extractModal.form.display_name"
          placeholder="留空则与模板名称相同"
        />
        <CTextarea label="描述" v-model="extractModal.form.description" :rows="2" />
        <CRow>
          <CCol sm="3">
            <CSelect label="地址族" v-model="extractModal.form.afi" :options="['ipv4','ipv6','dual']" />
          </CCol>
          <CCol sm="3">
            <CSelect label="协议" v-model="extractModal.form.protocol" :options="['bgp','ospf','rip','static','mixed']" />
          </CCol>
          <CCol sm="3">
            <CSelect label="方向" v-model="extractModal.form.direction" :options="['in','out','both']" />
          </CCol>
          <CCol sm="3">
            <CSelect label="Scope" v-model="extractModal.form.scope" :options="[
              { value: 'global', label: 'Global 全局' },
              { value: 'local', label: 'Local 本机' }
            ]" />
          </CCol>
        </CRow>
        <CAlert color="info" fade>
          从一台路由器上抽取已有的 filter 规则，录入为系统模板。<strong>只需抽取一次</strong>，
          之后通过「关联路由器」把模板关联到其他路由器，再点「同步」即可批量推送。
          同步时系统会自动给路由器上的规则打上 <code>[managed:scope] uuid=xxx</code> comment 标记。
        </CAlert>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="extractModal.show = false">取消</CButton>
          <CButton color="success" :disabled="extractModal.loading" @click="submitExtract">
            {{ extractModal.loading ? '导入中...' : '开始导入' }}
          </CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 通用确认 Modal -->
    <CModal :show.sync="confirmModal.show" :title="confirmModal.title" color="primary">
      <p>{{ confirmModal.message }}</p>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="confirmModal.show = false">取消</CButton>
          <CButton color="success" @click="confirmModal.callback(); confirmModal.show = false">确认</CButton>
        </CButtonGroup>
      </template>
    </CModal>
  </div>
</template>

<script>
export default {
  name: 'RoutingFilterList',
  data() {
    return {
      loading: false,
      allChains: [],
      activeScope: '',
      fields: [
        { key: 'name', label: 'Chain 名称', _style: 'min-width:200px; width:25%' },
        { key: 'tags', label: '属性', _style: 'width:18%' },
        { key: 'rule_count', label: '规则数', _style: 'width:10%' },
        { key: 'scope', label: 'Scope', _style: 'width:12%' },
        { key: 'enabled', label: '状态', _style: 'width:10%' },
        { key: 'action', label: '操作', _style: 'width:25%' }
      ],
      createModal: {
        show: false,
        form: {
          name: '', display_name: '', description: '',
          afi: 'ipv4', protocol: 'bgp', direction: 'out',
          scope: 'global'
        }
      },
      allRouters: [],
      confirmModal: {
        show: false,
        title: '确认',
        message: '',
        callback: () => {}
      },
      extractModal: {
        show: false,
        loading: false,
        loadingChains: false,
        chainOptions: [],
        chainError: '',
        form: {
          router_id: '',
          chain_name: '',
          name: '',
          display_name: '',
          description: '',
          afi: 'ipv4', protocol: 'bgp', direction: 'out',
          scope: 'global'
        }
      }
    }
  },
  computed: {
    chainList() {
      if (!this.activeScope) return this.allChains
      return this.allChains.filter(c => c.scope === this.activeScope)
    },
    routerOptions() {
      return this.allRouters.map(r => ({
        value: r.id,
        label: `${r.name} (${r.host})`
      }))
    },
    globalCount() {
      return this.allChains.filter(c => c.scope === 'global').length
    },
    localCount() {
      return this.allChains.filter(c => c.scope === 'local').length
    }
  },
  mounted() {
    this.loadList()
    this.loadAllRouters()
  },
  methods: {
    async loadAllRouters() {
      try {
        const res = await this.$axios.get('user/router')
        if (res.data.code === 200) {
          const own = res.data.userRouter || []
          const authed = (res.data.authedRouter || []).map(a => a.router).filter(Boolean)
          this.allRouters = [...own, ...authed]
        }
      } catch (e) { /* ignore */ }
    },
    async loadList() {
      this.loading = true
      try {
        const res = await this.$axios.get('user/routing/filter/chains')
        if (res.data.code === 200) {
          this.allChains = res.data.data
        } else {
          this.$toasted.show(res.data.msg || '加载失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.loading = false
    },
    openCreateModal() {
      this.createModal.form = {
        name: '', display_name: '', description: '',
        afi: 'ipv4', protocol: 'bgp', direction: 'out',
        scope: this.activeScope || 'global'
      }
      this.createModal.show = true
    },
    async submitCreate() {
      try {
        const res = await this.$axios.post('user/routing/filter/chains', this.createModal.form)
        if (res.data.code === 200) {
          this.$toasted.show('创建成功', { type: 'success' })
          this.createModal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '创建失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
    },
    openExtractModal() {
      this.extractModal.form = {
        router_id: '',
        chain_name: '',
        name: '',
        display_name: '',
        description: '',
        afi: 'ipv4', protocol: 'bgp', direction: 'out',
        scope: 'global'
      }
      this.extractModal.chainOptions = []
      this.extractModal.chainError = ''
      this.extractModal.show = true
    },
    async onExtractRouterChange() {
      this.extractModal.form.chain_name = ''
      this.extractModal.form.name = ''
      this.extractModal.form.display_name = ''
      this.extractModal.chainOptions = []
      this.extractModal.chainError = ''
      if (!this.extractModal.form.router_id) return

      this.extractModal.loadingChains = true
      try {
        const res = await this.$axios.get(`user/routing/filter/routers/${this.extractModal.form.router_id}/chains`)
        if (res.data.code === 200) {
          this.extractModal.chainOptions = (res.data.data || []).map(name => ({
            value: name,
            label: name
          }))
          if (this.extractModal.chainOptions.length === 0) {
            this.extractModal.chainError = '该路由器上没有 filter chain'
          }
        } else {
          this.extractModal.chainError = res.data.msg || '拉取 chain 列表失败'
        }
      } catch (e) {
        this.extractModal.chainError = '网络错误: ' + e.message
      }
      this.extractModal.loadingChains = false
    },
    onChainSelected() {
      this.extractModal.form.name = this.extractModal.form.chain_name
    },
    async submitExtract() {
      const f = this.extractModal.form
      if (!f.router_id || !f.chain_name || !f.name) {
        this.$toasted.show('源路由器、Chain 名称、模板名称必填', { type: 'error' })
        return
      }
      this.extractModal.loading = true
      try {
        const res = await this.$axios.post('user/routing/filter/chains/extract-from-router', f)
        if (res.data.code === 200) {
          const d = res.data.data
          this.$toasted.show(`导入成功：${d.rule_count} 条规则，来源 ${d.source_router}/${d.source_chain}`, { type: 'success' })
          this.extractModal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '导入失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.extractModal.loading = false
    },
    showConfirm(title, message, callback) {
      this.confirmModal.title = title
      this.confirmModal.message = message
      this.confirmModal.callback = callback
      this.confirmModal.show = true
    },
    goDetail(id) {
      this.$router.push('/routing/filter/chain/' + id)
    },
    async syncChain(id) {
      this.showConfirm('确认同步', '确认同步此 chain 到所有目标路由器？', async () => {
        try {
          const res = await this.$axios.post(`user/routing/filter/sync/chain/${id}`)
          if (res.data.code === 200) {
            const results = res.data.data.results || []
            const total = results.length
            const failed = results.reduce((s, r) => s + (r.failed || 0), 0)
            this.$toasted.show(`同步完成，路由器数: ${total}，失败操作: ${failed}`, {
              type: failed > 0 ? 'warning' : 'success'
            })
          } else {
            this.$toasted.show(res.data.msg || '同步失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    },
    async toggleChain(item) {
      try {
        const res = await this.$axios.post(`user/routing/filter/chains/${item.id}/toggle`)
        if (res.data.code === 200) {
          item.enabled = res.data.data.enabled
          this.$toasted.show('已切换', { type: 'success' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
    },
    async deleteChain(item) {
      this.showConfirm('确认删除', `确认删除 chain "${item.name}"？此操作会同时删除所有规则模板，但路由器上的规则需手动同步删除。`, async () => {
        try {
          const res = await this.$axios.delete(`user/routing/filter/chains/${item.id}/delete`)
          if (res.data.code === 200) {
            this.$toasted.show('已删除', { type: 'success' })
            this.loadList()
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    }
  }
}
</script>

<style scoped>
.table >>> td,
.table >>> th {
  vertical-align: middle !important;
}
.table >>> td.text-center,
.table >>> th[style*="text-align:center"] {
  text-align: center !important;
}
</style>
